import { FC, useCallback, useState } from 'react'
import cn from 'classnames'
import { NavLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '@src/components/Button/Button'
import style from '@src/components/InfoProfileBlock/InfoProfileBlock.module.scss'
import { AppDispatch } from '@src/store/store'
import { selectUserInfo, selectUserIsLoadingChangePhones } from '@src/store/ducks/user/selectors'
import { ReactComponent as Edit } from '@src/assets/interface_edit.svg'
import { ReactComponent as Ok } from '@src/assets/interface-ок.svg'
import { ReactComponent as Cross } from '@src/assets/interface-cross.svg'
import { FieldFormik } from '@src/components/common/FieldFormik/FieldFormik'
import { useField, useFormikContext } from 'formik'
import { changeEmail, changePhone } from '@src/store/ducks/user/thunks'
import { SetNewPhoneType } from '@src/api/user-api'
import * as Yup from 'yup'
import { email, phoneRegexp } from '@src/lib/regex'
import { validateForm } from '@src/messages/validateForm'

type InfoProfileBlockProps = {
  className?: string
  title?: string
  name: 'emails' | 'phones' | 'address'
}

const validationSchema = Yup.object().shape({
  phones: Yup.string().matches(phoneRegexp, validateForm.phone)
    .required(validateForm.required),
  emails: Yup.string()
    .matches(email, validateForm.email)
    .required(validateForm.required),
})

export const InfoProfileBlock: FC<InfoProfileBlockProps> = ({
  title, className, name,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const isLoading = useSelector(selectUserIsLoadingChangePhones)
  const [edit, setEdit] = useState<number[]>([])
  const user = useSelector(selectUserInfo)
  const [isAdd, setIsAdd] = useState<boolean>(false)
  const [field] = useField(name)
  const addPhoneField = useField('phone')
  const addEmailField = useField('email')

  const { setFieldValue } = useFormikContext<any>()

  const handleEdit = useCallback((index: number) => {
    // @ts-ignore
    const isValidField = validationSchema.fields[name].isValidSync(field.value[index])

    if (name === 'phones' && isValidField) {
      dispatch(changePhone({
        oldPhone: user.phones[index],
        type: SetNewPhoneType.MODILE,
        phone: field.value[index],
      }))
      setEdit((prevState) => prevState.filter((item) => item !== index))
    }

    if (name === 'emails' && isValidField) {
      dispatch(changeEmail({
        oldEmail: user.emails[index],
        email: field.value[index],
      }))
      setEdit((prevState) => prevState.filter((item) => item !== index))
    }
  }, [dispatch, field.value, name, user])

  const addPhone = useCallback(() => {
    const isValidField = validationSchema.fields.phones.isValidSync(addPhoneField[0].value)
    const isUnique = !user[name].includes(addPhoneField[0].value.replace(/\D+/g, '')) // todo добавить в схему

    if (isValidField && isUnique) {
      dispatch(changePhone({
        oldPhone: '',
        type: SetNewPhoneType.MODILE,
        phone: addPhoneField[0].value || '',
      }))
      setIsAdd(false)
    }
  }, [addPhoneField, dispatch, name, user])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addEmail = () => {
    const isValidField = validationSchema.fields.emails.isValidSync(addEmailField[0].value)
    const isUnique = !user[name].includes(addEmailField[0].value) // todo добавить в схему
    console.log(isValidField, isUnique, user[name], addEmailField[0].value)
    if (isValidField && isUnique) {
      dispatch(changeEmail({
        oldEmail: '',
        email: addEmailField[0].value || '',
      }))
      setIsAdd(false)
    }
  }

  const closeAdd = useCallback(() => {
    setIsAdd(false)
    setFieldValue('phone', '')
  }, [setFieldValue])

  return (
    <div className={cn(style.infoBlock, className)}>
      <div className={style.firstBox}>
        <div className={style.title}>{title}</div>
        {name === 'address' ? (
          <div className={style.addresses}>{field.value}</div>
        ) : (
          <div className={style.addresses}>
            {isAdd && (
              <FieldFormik
                placeholder={name === 'phones' ? '+375 (ХХ) ХХХ ХХ ХХ' : 'some@email.com'}
                name={name === 'phones' ? 'phone' : 'email'}
                // eslint-disable-next-line max-len
                label={name === 'phones' ? 'Введите номер телефона и нажмите кнопку \'Добавить\'' : 'Введите адрес электронной почты и нажмите кнопку \'Добавить\''}
                mask={name === 'phones' ? '+{375} (00) 000-00-00' : undefined}
                className={cn(style.active)}
              />
            )}

            {user[name].map((el: string, index: number) => {
              const isEdit = edit.some((item) => item === index)

              return (
                <div
                  key={el}
                  className={style.address}
                >
                  <div className={cn(style.btn, (isAdd) && style.blockBtn)}>
                    {
                      !isEdit
                        ? <Edit onClick={() => { setEdit([...edit, index]) }} />
                        : (
                          <>
                            {user[name].includes(field.value[index].replace(/\D+/g, '')) ? (
                              <Cross onClick={() => setEdit(edit.filter((item) => item !== index))} />
                            ) : (
                              <Ok onClick={() => handleEdit(index)} />
                            )}
                          </>
                        )
                    }
                  </div>
                  <FieldFormik
                    name={`${name}[${index}]`}
                    className={cn(!isEdit && style.readOnly, isEdit && style.field)}
                    disabled={!isEdit}
                    mask={name === 'phones' ? '+{375} (00) 000-00-00' : undefined}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className={style.secondBox}>
        {(() => {
          switch (name) {
            case 'address':
              return (
                <NavLink
                  to="/moving-new-address"
                >
                  <Button
                    className={style.btn}
                    type="textSecondary"
                  >
                    Переезд на новый адрес
                  </Button>
                </NavLink>
              )
            case 'phones':
              return (
                <>
                  <Button
                    loading={isLoading}
                    htmlType="button"
                    onClickFunc={isAdd ? addPhone : () => setIsAdd(true)}
                    className={cn(isLoading ? style.loader : style.btn)}
                    type="textSecondary"
                  >
                    {!isAdd ? 'Добавить номер телефона' : 'Добавить'}
                  </Button>
                  {isAdd && (
                    <Button
                      htmlType="button"
                      className={cn(style.btn, style.btnMt)}
                      type="textSecondary"
                      onClickFunc={closeAdd}
                    >
                      Отмена
                    </Button>
                  )}

                </>
              )
            /* case 'emails':
              return (
                <Button
                  loading={isLoading}
                  htmlType="button"
                  onClickFunc={isAdd ? addEmail : () => setIsAdd(true)}
                  className={cn(isLoading ? style.loader : style.btn)}
                  type="textSecondary"
                >
                  {!isAdd ? 'Добавить адрес эл. почты' : 'Добавить'}
                </Button>
              ) */
            default:
              return null
          }
        })()}
      </div>
    </div>
  )
}
